import React, { useState, useRef } from 'react'
import html2canvas from 'html2canvas'
import { api } from '../../utils/api'

const FEEDBACK_ICON = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
  </svg>
)

const CLOSE_ICON = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
)

const STATUS_LABELS = {
  backlog: 'Backlog',
  todo: 'To Do',
  in_progress: 'In Progress',
  in_review: 'In Review',
  done: 'Done'
}

const STATUS_COLORS = {
  backlog: '#6b7280',
  todo: '#3b82f6',
  in_progress: '#f59e0b',
  in_review: '#8b5cf6',
  done: '#10b981'
}

function captureFormState() {
  const state = {}
  const inputs = document.querySelectorAll('input, select, textarea')
  for (const el of inputs) {
    if (el.closest('.feedback-modal') || el.type === 'password' || el.type === 'hidden' || el.type === 'file') continue
    const isSelect = el.tagName === 'SELECT'
    const isCheckbox = el.type === 'checkbox' || el.type === 'radio'
    const firstOptionText = isSelect && el.options.length > 0 ? el.options[0].text : null
    const parentLabel = isCheckbox ? el.closest('label')?.textContent?.trim() : null
    const siblingLabel = el.parentElement?.querySelector('label')?.textContent?.trim()
    const name = el.name || el.id || el.getAttribute('aria-label') || el.placeholder || firstOptionText || parentLabel || siblingLabel
    if (!name) continue
    if (isCheckbox) {
      state[name] = el.checked ? 'on' : 'off'
    } else {
      if (!el.value) continue
      state[name] = isSelect ? el.options[el.selectedIndex].text : el.value
    }
  }
  return state
}

async function captureScreenshot() {
  try {
    const canvas = await html2canvas(document.body, {
      logging: false,
      useCORS: true,
      scale: 1,
      windowWidth: document.documentElement.scrollWidth,
      windowHeight: document.documentElement.scrollHeight,
      scrollX: 0,
      scrollY: -window.scrollY
    })
    const tempCanvas = document.createElement('canvas')
    tempCanvas.width = canvas.width
    tempCanvas.height = canvas.height
    const tempCtx = tempCanvas.getContext('2d')
    tempCtx.fillStyle = '#FFFFFF'
    tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height)
    tempCtx.drawImage(canvas, 0, 0)
    return tempCanvas.toDataURL('image/jpeg', 0.7)
  } catch (error) {
    console.error('Screenshot capture failed:', error)
    return null
  }
}

function formatDate(dateStr) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

const SUMMARY_PREFIX = '[Prism Feedback] '

function FeedbackButton() {
  const [isOpen, setIsOpen] = useState(false)
  const [view, setView] = useState('form') // 'form' or 'list'
  const [description, setDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [toast, setToast] = useState(null)
  const [tickets, setTickets] = useState([])
  const [loadingTickets, setLoadingTickets] = useState(false)
  const textareaRef = useRef(null)

  function handleOpen() {
    setIsOpen(true)
    setView('form')
    setDescription('')
    setTimeout(() => textareaRef.current?.focus(), 100)
  }

  function handleClose() {
    if (submitting) return
    setIsOpen(false)
    setDescription('')
    setView('form')
  }

  async function loadTickets() {
    setView('list')
    setLoadingTickets(true)
    try {
      const response = await api.get('/api/feedback')
      setTickets(response.tickets || [])
    } catch (error) {
      console.error('Failed to load tickets:', error)
      setTickets([])
    } finally {
      setLoadingTickets(false)
    }
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!description.trim() || submitting) return

    setSubmitting(true)
    try {
      const [screenshot, formState] = await Promise.all([
        captureScreenshot(),
        Promise.resolve(captureFormState())
      ])

      const response = await api.post('/api/feedback', {
        description: description.trim(),
        url: window.location.href,
        screenshot_base64: screenshot,
        form_state: formState
      })

      setIsOpen(false)
      setDescription('')
      setView('form')
      showToast(`Ticket #${response.ticket_id} created`, 'success')
    } catch (error) {
      console.error('Feedback submission failed:', error)
      showToast('Failed to submit feedback. Please try again.', 'error')
    } finally {
      setSubmitting(false)
    }
  }

  function showToast(message, type) {
    setToast({ message, type })
    setTimeout(() => setToast(null), 4000)
  }

  return (
    <>
      {!isOpen && (
        <button
          className="feedback-fab"
          onClick={handleOpen}
          title="Report a problem or suggest an idea"
        >
          {FEEDBACK_ICON}
        </button>
      )}

      {isOpen && (
        <div className="feedback-overlay" onClick={handleClose}>
          <div className="feedback-modal" onClick={e => e.stopPropagation()}>
            <div className="feedback-modal-header">
              <h3>{view === 'form' ? 'Report Problem / Suggest Idea' : 'Your Requests'}</h3>
              <button className="feedback-close-btn" onClick={handleClose}>
                {CLOSE_ICON}
              </button>
            </div>

            {view === 'form' && (
              <>
                <form onSubmit={handleSubmit}>
                  <textarea
                    ref={textareaRef}
                    className="feedback-textarea"
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    placeholder="Describe what you're seeing or what you'd like..."
                    maxLength={5000}
                    rows={5}
                  />
                  <div className="feedback-modal-footer">
                    <span className="text-sm text-muted">
                      {description.length}/5000
                    </span>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={!description.trim() || submitting}
                    >
                      {submitting ? 'Submitting...' : 'Submit'}
                    </button>
                  </div>
                </form>
                <div style={{ padding: '0 16px 12px', textAlign: 'center' }}>
                  <button
                    onClick={loadTickets}
                    style={{
                      background: 'none', border: 'none', color: '#3b82f6',
                      cursor: 'pointer', fontSize: 12, textDecoration: 'underline',
                      padding: 0
                    }}
                  >
                    View my requests
                  </button>
                </div>
              </>
            )}

            {view === 'list' && (
              <div style={{ padding: '0 16px 16px' }}>
                {loadingTickets ? (
                  <p style={{ textAlign: 'center', color: '#6b7280', fontSize: 13, padding: '20px 0' }}>Loading...</p>
                ) : tickets.length === 0 ? (
                  <p style={{ textAlign: 'center', color: '#6b7280', fontSize: 13, padding: '20px 0' }}>No requests yet.</p>
                ) : (
                  <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                    {tickets.map(t => (
                      <div key={t.id} style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '8px 0', borderBottom: '1px solid #e5e7eb', fontSize: 13
                      }}>
                        <span style={{ color: '#6b7280', fontWeight: 600, flexShrink: 0, minWidth: 36 }}>
                          #{t.id}
                        </span>
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {(t.summary || '').replace(SUMMARY_PREFIX, '')}
                        </span>
                        <span style={{
                          flexShrink: 0, fontSize: 11, fontWeight: 600,
                          padding: '2px 8px', borderRadius: 10,
                          color: '#fff',
                          background: STATUS_COLORS[t.status] || '#6b7280'
                        }}>
                          {STATUS_LABELS[t.status] || t.status}
                        </span>
                        <span style={{ flexShrink: 0, color: '#9ca3af', fontSize: 11 }}>
                          {formatDate(t.created_at)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ paddingTop: 12, textAlign: 'center' }}>
                  <button
                    onClick={() => { setView('form'); setTimeout(() => textareaRef.current?.focus(), 100) }}
                    style={{
                      background: 'none', border: 'none', color: '#3b82f6',
                      cursor: 'pointer', fontSize: 12, textDecoration: 'underline',
                      padding: 0
                    }}
                  >
                    New request
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {toast && (
        <div className={`feedback-toast feedback-toast-${toast.type}`}>
          {toast.message}
        </div>
      )}
    </>
  )
}

export default FeedbackButton
